import { useMemo, useState } from 'react';
import { CalendarClock } from 'lucide-react';
import type { StaffSchedule } from '../../types/staff';
import { StaffSection } from './StaffSection';
import { formatUtcHour, utcHourToLocal } from '../../utils/staffProfile';
import { cn } from '../../utils/cn';

interface StaffScheduleCardProps {
  schedule: StaffSchedule;
}

const pct = (value: number) => `${Math.round(value * 100)}%`;

/** Reply-hour histogram (24 UTC buckets) with active-hour band, peak marker and the steward's caveat. */
export const StaffScheduleCard = ({ schedule }: StaffScheduleCardProps) => {
  const [showLocal, setShowLocal] = useState(false);
  const counts = schedule.messages_by_hour_utc ?? [];
  const max = Math.max(1, ...counts);
  const active = useMemo(() => new Set(schedule.active_hours_utc ?? []), [schedule.active_hours_utc]);
  const label = (hour: number) => formatUtcHour(showLocal ? utcHourToLocal(hour) : hour);

  const toggle = (
    <div className="inline-flex rounded-lg border border-glass-200/20 p-0.5 text-[11px] font-mono" role="group" aria-label="Time zone">
      {[
        { id: false, label: 'UTC' },
        { id: true, label: 'Local' },
      ].map((opt) => (
        <button
          key={opt.label}
          type="button"
          onClick={() => setShowLocal(opt.id)}
          aria-pressed={showLocal === opt.id}
          className={cn(
            'px-2.5 h-6 rounded-md transition-colors',
            showLocal === opt.id ? 'bg-accent-500/20 text-accent-300' : 'text-glass-400 hover:text-white',
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  return (
    <StaffSection title="Reply-hour pattern" icon={CalendarClock} actions={toggle}>
      <p className="text-[11px] font-mono text-glass-500 mb-4">
        {schedule.samples} samples · {schedule.days} days measured
      </p>

      {schedule.known ? (
        <>
          <div className="relative">
            <div className="grid grid-cols-[repeat(24,minmax(0,1fr))] gap-[3px] items-end h-24" role="img" aria-label="Replies per hour of day">
              {counts.map((count, hour) => {
                const isPeak = schedule.peak_hour_utc === hour;
                const isActive = active.has(hour);
                return (
                  <div
                    key={hour}
                    title={`${label(hour)} — ${count} repl${count === 1 ? 'y' : 'ies'}${isActive ? ' · active hour' : ''}${isPeak ? ' · peak' : ''}`}
                    className={cn('relative h-full flex items-end rounded-t-[4px] rounded-b-sm', isActive ? 'bg-accent-500/10' : 'bg-glass-100/5')}
                  >
                    <div
                      className={cn(
                        'w-full rounded-t-[4px] transition-all duration-500',
                        isPeak
                          ? 'bg-gradient-to-t from-accent-500 to-accent-300 shadow-[0_0_14px_rgb(var(--accent-t-400-rgb)/0.5)]'
                          : isActive
                            ? 'bg-gradient-to-t from-accent-600/80 to-accent-400/80'
                            : count > 0
                              ? 'bg-glass-300/40'
                              : 'bg-transparent',
                      )}
                      style={{ height: `${count > 0 ? Math.max(6, (count / max) * 100) : 0}%` }}
                    />
                    {isPeak && (
                      <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] font-mono text-accent-300 tabular-nums whitespace-nowrap">
                        {count}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-1.5 flex justify-between text-[10px] font-mono text-glass-500">
              {[0, 6, 12, 18, 23].map((h) => (
                <span key={h}>{label(h)}</span>
              ))}
            </div>
          </div>
          <dl className="mt-4 grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-3">
            {[
              ['Active band', schedule.start_hour_utc !== null && schedule.end_hour_utc !== null ? `${label(schedule.start_hour_utc)} – ${label(schedule.end_hour_utc)}` : '—'],
              ['Peak hour', schedule.peak_hour_utc !== null ? label(schedule.peak_hour_utc) : '—'],
              ['Inside band', `${pct(schedule.share_in_window)} of replies`],
              ['Weekends', `${pct(schedule.weekend_share)} of replies`],
            ].map(([k, v]) => (
              <div key={k} className="rounded-lg bg-glass-100/5 border border-glass-200/10 px-3 py-2">
                <dt className="text-[10px] uppercase tracking-[0.14em] text-glass-500">{k}</dt>
                <dd className="text-sm font-semibold text-white mt-0.5 tabular-nums">{v}</dd>
              </div>
            ))}
          </dl>
        </>
      ) : (
        <>
          <div className="grid grid-cols-[repeat(24,minmax(0,1fr))] gap-[3px] h-10" aria-hidden="true">
            {Array.from({ length: 24 }, (_, i) => (
              <div key={i} className="rounded-sm border border-dashed border-glass-200/20" />
            ))}
          </div>
          <div className="mt-3 px-3 py-2 rounded-lg border border-dashed border-glass-200/20 bg-glass-100/5 text-xs text-glass-300">
            <b className="text-white">No reply-hour samples in this window.</b>
          </div>
        </>
      )}
      <p className="mt-4 text-[11px] text-glass-500 leading-relaxed">{schedule.caveat}</p>
    </StaffSection>
  );
};
